import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { definirPremioDoProduto, lerPremiosDoProduto, lerReais, reais } from '../../produtos';
import type { PremioNoPrograma, ProgramaPorBase } from '../../produtos';
import { mensagemDeFalha } from '../equipe/Equipe';

/**
 * PRÊMIO BASE DO PRODUTO no pré-vencido e no paradão (decisão do dono, 24/09/2026).
 *
 * Sem exceção vale o do programa. A mudança vale da PRÓXIMA semana (sábado): a semana que está correndo
 * fica com o prêmio com que foi lançada — o cheque e o lançamento sempre batem. A tela nunca
 * calcula nem confirma antes do servidor; queda de rede ao gravar é INCERTO (recarrega e confere).
 */

const NOME: Record<ProgramaPorBase, string> = { pre_vencido: 'Pré-vencido', paradao: 'Paradão' };
const diaMes = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const dinheiro = (v: string) => reais(Number(v).toFixed(2));

type Estado =
  | { fase: 'parado' }
  | { fase: 'salvando' }
  | { fase: 'confirmado'; desde: string }
  | { fase: 'recusado'; mensagem: string }
  | { fase: 'incerto' };

export function PremioNaBase({
  id,
  sessao,
  configura,
}: {
  id: string;
  sessao: Sessao;
  configura: boolean;
}) {
  const [programas, setProgramas] = useState<readonly PremioNoPrograma[] | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [editando, setEditando] = useState<ProgramaPorBase | null>(null);
  const [valor, setValor] = useState('');
  const [doPrograma, setDoPrograma] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [estado, setEstado] = useState<Estado>({ fase: 'parado' });

  const carregar = useCallback(async () => {
    setFalha(null);
    const r = await lerPremiosDoProduto(id);
    if (r.ok) setProgramas(r.dados.programas);
    else setFalha(mensagemDeFalha(r.falha));
  }, [id]);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function gravar(p: PremioNoPrograma) {
    const premio = doPrograma ? null : lerReais(valor);
    if (!doPrograma && premio === null)
      return setEstado({ fase: 'recusado', mensagem: 'Digite o prêmio em reais (ex.: 5,00).' });
    setEstado({ fase: 'salvando' });
    const r = await definirPremioDoProduto(sessao, id, {
      programa: p.programa,
      premio,
      versaoEsperada: p.versao,
      motivo: motivo.trim(),
    });
    if (r.ok) {
      setEstado({ fase: 'confirmado', desde: r.dados.vigenteDesde });
      setEditando(null);
      await carregar();
      return;
    }
    if (r.falha.tipo === 'offline') {
      setEstado({ fase: 'incerto' });
      await carregar();
      return;
    }
    setEstado({ fase: 'recusado', mensagem: mensagemDeFalha(r.falha) });
  }

  if (falha)
    return (
      <div className="pd-pe" role="alert">
        Prêmio base indisponível: {falha}{' '}
        <button type="button" className="ac-link" onClick={() => void carregar()}>
          Tentar de novo
        </button>
      </div>
    );
  if (!programas) return <div className="pd-pe">Carregando o prêmio base…</div>;

  return (
    <div className="pd-condicoes">
      <div className="ac-rotulo-mono" style={{ marginBottom: 9 }}>
        PRÊMIO BASE · PRÉ-VENCIDO E PARADÃO
      </div>
      <div className="pd-condicoes-grade pd-premio-grade">
        {programas.map((p) => {
          const vale = p.vigente ?? p.doPrograma;
          return (
            <div key={p.programa} className="pd-condicao">
              <div className="pd-condicao-titulo">{NOME[p.programa]}</div>
              <div className="pd-premio-valor">{dinheiro(vale)}</div>
              <div className="pd-condicao-texto">
                {p.vigente ? 'deste produto' : 'o do programa'}
                {p.agendado
                  ? ` · a partir de ${diaMes(p.agendado.desde)}: ${p.agendado.premio ? dinheiro(p.agendado.premio) : `o do programa (${dinheiro(p.doPrograma)})`}`
                  : ''}
              </div>
              {configura && editando !== p.programa && (
                <button
                  type="button"
                  className="ac-link"
                  onClick={() => {
                    setEditando(p.programa);
                    setValor('');
                    setDoPrograma(false);
                    setMotivo('');
                    setEstado({ fase: 'parado' });
                  }}
                >
                  Mudar
                </button>
              )}
              {editando === p.programa && (
                <div className="pd-premio-edicao">
                  <label className="pd-premio-linha">
                    <input
                      type="checkbox"
                      checked={doPrograma}
                      onChange={(e) => setDoPrograma(e.target.checked)}
                    />
                    usar o do programa ({dinheiro(p.doPrograma)})
                  </label>
                  {!doPrograma && (
                    <input
                      className="pd-premio-campo"
                      value={valor}
                      inputMode="decimal"
                      placeholder="R$ 0,00"
                      aria-label={`Prêmio base no ${NOME[p.programa]}`}
                      onChange={(e) => setValor(e.target.value)}
                    />
                  )}
                  <input
                    className="pd-premio-campo"
                    value={motivo}
                    maxLength={500}
                    placeholder="Por que está mudando"
                    aria-label="Motivo"
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                  <div className="pd-condicao-texto">
                    Vale da próxima semana: a semana que está correndo continua com o prêmio atual.
                  </div>
                  <div className="pd-premio-linha">
                    <button
                      type="button"
                      className="ac-botao ac-botao-primario"
                      disabled={estado.fase === 'salvando'}
                      onClick={() => void gravar(p)}
                    >
                      {estado.fase === 'salvando' ? 'Gravando…' : 'Gravar'}
                    </button>
                    <button
                      type="button"
                      className="ac-botao"
                      disabled={estado.fase === 'salvando'}
                      onClick={() => setEditando(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {estado.fase === 'confirmado' && (
        <div className="pd-pe" role="status">
          Gravado. Vale a partir de {diaMes(estado.desde)}.
        </div>
      )}
      {estado.fase === 'recusado' && (
        <div className="pd-pe pd-indisponivel" role="alert">
          {estado.mensagem}
        </div>
      )}
      {estado.fase === 'incerto' && (
        <div className="pd-pe" role="alert">
          A conexão caiu e não sabemos se gravou. Os valores acima foram recarregados: confira antes
          de tentar de novo.
        </div>
      )}
    </div>
  );
}
